export type CabinetRole = 'admin' | 'trader' | 'merchant' | 'support';

const CABINET_ROLES: CabinetRole[] = ['admin', 'trader', 'merchant', 'support'];

function trimTrailingSlash(value: string): string {
    return value.replace(/\/+$/, '');
}

function configuredRole(): CabinetRole | null {
    const role = process.env.REACT_APP_CABINET_ROLE?.trim().toLowerCase();
    return CABINET_ROLES.includes(role as CabinetRole) ? role as CabinetRole : null;
}

function inferredBaseDomain(): string {
    const labels = window.location.hostname.toLowerCase().split('.');
    return CABINET_ROLES.includes(labels[0] as CabinetRole) || labels[0] === 'api'
        ? labels.slice(1).join('.')
        : labels.join('.');
}

function isLocalHostname(): boolean {
    return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
}

export function usesDedicatedCabinetHost(): boolean {
    const hostRole = window.location.hostname.toLowerCase().split('.')[0];
    return configuredRole() !== null || CABINET_ROLES.includes(hostRole as CabinetRole);
}

export function currentCabinetRole(): CabinetRole | null {
    const envRole = configuredRole();
    if (envRole) return envRole;

    const hostname = window.location.hostname.toLowerCase();
    const role = hostname.split('.')[0];

    if (CABINET_ROLES.includes(role as CabinetRole)) return role as CabinetRole;

    const pathRole = window.location.pathname.split('/').filter(Boolean)[0];
    return CABINET_ROLES.includes(pathRole as CabinetRole) ? pathRole as CabinetRole : null;
}

export function cabinetPath(role: CabinetRole, path: string): string {
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    return usesDedicatedCabinetHost() ? normalizedPath : `/${role}${normalizedPath}`;
}

export function publicWebUrl(): string {
    const configuredUrl = process.env.REACT_APP_PUBLIC_WEB_URL?.trim();
    if (configuredUrl) return trimTrailingSlash(configuredUrl);
    if (isLocalHostname()) return window.location.origin;
    return `${window.location.protocol}//${inferredBaseDomain()}`;
}

export function paymentWebUrl(): string {
    const configuredUrl = process.env.REACT_APP_PAYMENT_WEB_URL?.trim();
    return configuredUrl ? trimTrailingSlash(configuredUrl) : publicWebUrl();
}

export function apiUrl(): string {
    const configuredUrl = process.env.REACT_APP_API_URL?.trim();
    if (configuredUrl) return trimTrailingSlash(configuredUrl);
    if (isLocalHostname()) return `http://${window.location.hostname}:8000`;
    return `${window.location.protocol}//api.${inferredBaseDomain()}`;
}

export function cabinetUrl(role: CabinetRole, path: string): string {
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    const envName = `REACT_APP_${role.toUpperCase()}_WEB_URL`;
    const configuredUrl = process.env[envName]?.trim();
    if (configuredUrl) return `${trimTrailingSlash(configuredUrl)}${normalizedPath}`;
    if (!usesDedicatedCabinetHost()) return `${window.location.origin}${cabinetPath(role, normalizedPath)}`;
    if (configuredRole() === role) return `${window.location.origin}${normalizedPath}`;
    return `${window.location.protocol}//${role}.${inferredBaseDomain()}${normalizedPath}`;
}
